import React, { useEffect, useMemo, useState } from 'react'
import { DistributionChannel } from '@phrasee/phrasee-typings/Graphql/interfaces'
import {
  AuthType,
  EmbedEvent,
  HostEvent,
  init,
  RuntimeFilterOp,
} from '@thoughtspot/visual-embed-sdk'
import {
  LiveboardEmbed,
  useEmbedRef,
} from '@thoughtspot/visual-embed-sdk/lib/src/react'
import { DEFAULT_VARIANT_STATUS_FILTER } from 'workflow/common/constants'

import { errorToast } from 'common/components/toastNotification'
import { useAppSelector } from 'common/hooks/redux'

const THOUGHT_SPOT_BASE_URL = 'https://jacquard.thoughtspot.cloud'
const CONNECT_INTERNAL = process.env.REACT_APP_CONNECT_INTERNAL_END_POINT

interface Props {
  userId: string
}

const ThoughspotGraph = React.memo(({ userId }: Props) => {
  const campaignLiveBoardId = process.env.REACT_APP_CAMPAIGN_LIVEBOARD_ID
  const campaignId = useAppSelector(
    (state) => state.campaignStates.currentCampaignId
  )
  const campaignDistributionChannel = useAppSelector(
    (state) =>
      state.campaignStates?.campaignData?.campaign_configuration
        ?.distribution_channel
  )
  const ref = useEmbedRef<typeof LiveboardEmbed>()
  const [isLoading, setIsLoading] = useState(true)
  const [hasError, setHasError] = useState(false)
  const [isInitialized, setIsInitialized] = useState(false)

  useEffect(() => {
    const USER_TOKEN_URL = `v1/thoughtspot/users/${userId}/token`
    try {
      init({
        thoughtSpotHost: THOUGHT_SPOT_BASE_URL,
        authType: AuthType.TrustedAuthTokenCookieless,
        authEndpoint: `${CONNECT_INTERNAL}/${USER_TOKEN_URL}`,
        callPrefetch: true,
      })
      setIsInitialized(true)
    } catch (error) {
      setHasError(true)
      errorToast('Failed to initialize ThoughtSpot. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }, [userId])

  function metricNameFromDistributionType(
    distributionType: DistributionChannel
  ) {
    const mapping = {
      'num. clicks': [
        'email',
        'email_in_body',
        'social_ad',
        'web_and_app',
        'landing_page',
        'facebook',
        'adwords',
      ],
      'num. direct opens': ['push_notification', 'web_push'],
      'num. opens': ['sms'],
      'num. click button 1': ['content_card'],
      'num. impression': ['in_app_message'],
    }
    const key = Object.keys(mapping).find((key) =>
      mapping[key].includes(distributionType)
    )
    return key
  }

  const runtimeFilters = useMemo(
    () => [
      {
        columnName: 'CAMPAIGN ID',
        operator: RuntimeFilterOp.EQ,
        values: [campaignId],
      },
      {
        columnName: 'STATUS',
        operator: RuntimeFilterOp.IN,
        values: DEFAULT_VARIANT_STATUS_FILTER,
      },
    ],
    [campaignId]
  )

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const updateFilter = () => {
    const metricName = metricNameFromDistributionType(
      campaignDistributionChannel
    )
    ref.current.trigger(HostEvent.UpdateFilters, {
      filter: {
        column: 'Label',
        oper: 'EQ',
        values: [metricName],
      },
    })
    if (ref.current) {
      ref.current.off(EmbedEvent.Data, updateFilter)
    }
  }

  useEffect(() => {
    if (ref.current) {
      ref.current.on(EmbedEvent.Data, updateFilter)
    }
  }, [ref, updateFilter])

  return (
    <div
      style={{
        height: 850,
      }}
    >
      {isLoading && <div>Loading...</div>}
      {hasError && <div>Unable to load visualization</div>}
      {isInitialized && !hasError && (
        <>
          <LiveboardEmbed
            frameParams={{
              height: 850,
              width: '100%',
            }}
            ref={ref}
            liveboardId={campaignLiveBoardId}
            runtimeFilters={runtimeFilters}
            visibleActions={[]}
            customizations={{
              style: {
                customCSS: {
                  variables: {
                    '--ts-var-viz-background': '#f6efe0',
                    '--ts-var-root-background': '#FFFDF9',
                    '--ts-var-chip-background': '#eaedf2',
                    '--ts-var-chip--hover-background': '#FFE661',
                  },
                },
              },
            }}
          />
        </>
      )}
    </div>
  )
})

export default ThoughspotGraph
